import React, { useState, useEffect, useRef } from "react";
import API_BASE_URL from "../../config/api";

const BankDetails = ({ userId }) => {
  const [bankName, setBankName] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankList, setBankList] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [notification, setNotification] = useState(null);

  // Track if bankName change was triggered by user selecting from dropdown
  const userSelectedBankRef = useRef(false);

  // Handle Bank dropdown change
  const onBankChange = (e) => {
    const selectedBank = e.target.value;
    userSelectedBankRef.current = true;
    setBankName(selectedBank);
  };

  // When bankName changes, if user selected bank, update IFSC to master IFSC
  useEffect(() => {
    if (userSelectedBankRef.current) {
      const bank = bankList.find((b) => String(b.id) === String(bankName));
      if (bank) {
        setIfscCode(bank.value);
      } else {
        setIfscCode("");
      }
      userSelectedBankRef.current = false;  // reset flag
    }
  }, [bankName]);

  const fetchUserBankDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/Employee/GetBankDetails/${userId}`);

      if (response.ok) {
        const data = await response.json();
        if (data) {
          setBankName(data.bankName || "");
          setIfscCode(data.ifscCode || "");
          setAccountNumber(data.accountNumber || "");
          setIsEditMode(true);  // We have existing data, so edit mode
        } else {
          setIsEditMode(false); // No data found, so create mode
        }
      } else {
        setIsEditMode(false); // No data found, create mode
      }
    } catch (err) {
      console.error("Failed to fetch user bank details:", err);
      setIsEditMode(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserBankDetails();
    }
  }, [userId]);


  const fetchBankList = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/Employee/BankDetails/1`);
      if (response.ok) {
        const data = await response.json();
        setBankList(data);

      } else {
        console.warn("No data found for userId:", userId);
      }
    } catch (err) {
      console.error("Failed to fetch employee data:", err);
    }
  };

  useEffect(() => {
    fetchBankList();
  }, []);

  // When IFSC changes, update bankName based on IFSC prefix
  useEffect(() => {
   
    if (ifscCode.length >= 4) {
      const prefix = ifscCode.substring(0, 4).toUpperCase();
  
      const bank = bankList.find(
        (b) => b.value && b.value.toUpperCase().startsWith(prefix)
      );
    
      if (bank ) {
        setBankName(bank.id);
      } else if (!bank) {
        setBankName("");
      }
    } else {
      setBankName("");
    }
  }, [ifscCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bankName || !accountNumber || !ifscCode) {
      setNotification({ type: "error", message: `Please fill all fields.` });
      return;
    }

    const payload = {
      EmployeeId: userId,
      BankName: bankName,
      AccountNumber: accountNumber,
      IFSCCode: ifscCode,
    };
    try {
      const response = await fetch(`${API_BASE_URL}/api/Employee/SaveBankDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const resData = await response.json();
        setNotification({ type: "success", message: `Bank Details saved successfully!` });
        // Optionally refresh data here
      } else {
        const errorText = await response.text();
        setNotification({ type: "error", message: `Error saving bank details: ${errorText}` });
      }
    } catch (err) {
      setNotification({ type: "error", message: `Network error: ${err.message}` });
    }
  };

  const closeNotification = () => setNotification(null);

  return (
    <form onSubmit={handleSubmit} className="w-100 space-y-6 ">
      <h2 className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] pb-3">
        Bank Details
      </h2>

      {notification && (
        <div
          className={`mb-4 p-4 rounded ${notification.type === "error"
            ? "bg-red-100 text-red-700"
            : "bg-green-100 text-green-700"
            }`}
        >
          <div className="flex justify-between items-center">
            <span>{notification.message}</span>
            <button onClick={closeNotification} className="font-bold text-xl leading-none">
              ×
            </button>
          </div>
        </div>
      )}

      <div className="flex space-x-4">
        <div className="flex-1">
          <label className="block mb-1 font-semibold">Bank Name</label>
          <select
            required
            value={bankName}
            onChange={onBankChange}
            className="form-input"
          >
            <option value="">Select Bank</option>
            {bankList.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.title}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label className="block mb-1 font-semibold">IFSC Code</label>
          <input
            type="text"
            value={ifscCode}
            onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
            className="form-input"
            placeholder="IFSC code (editable)"
          />
        </div>
      </div>

      {/* Account Number full width */}
      <div>
        <label className="block mb-1 font-semibold">Account Number</label>
        <input
          type="text"
          required
          value={accountNumber}
          onChange={(e) => {
            const onlyNums = e.target.value.replace(/\D/g, "");
            setAccountNumber(onlyNums);
          }}
          className="form-input"
          placeholder="Enter your account number"
        />
      </div>

      {/* Submit button right aligned */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={fetchUserBankDetails}
          className="cancel-btn"
        >
          Cancel
        </button>

        <button
          type="submit"
          className="btn-black"
        >
          {isEditMode ? "Update Bank Details" : "Save Bank Details"}
        </button>
      </div>

    </form>

  );
};

export default BankDetails;
